import { isEqual } from 'lodash';
import { RcFile } from 'antd/es/upload';
import moment from 'jalali-moment';

type Callable = () => any;

export const invoke = (...callbacks: Callable[]) => callbacks.forEach(c => c());

export const unify = (...callbacks: Callable[]) => () => invoke(...callbacks);

export const range = (min: number, max: number) => {
  const items = [];
  if (min < max) {
    for (let i = min; i <= max; i++) {
      items.push(i);
    }
  }
  else {
    for (let i = min; i >= max; i--) {
      items.push(i);
    }
  }
  return items;
};

/**
 * `max` not included
 */
export const randomInt = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min) + min);
};

export const flatArray = <T>(ary: any | any[]) => {
  let ret = [];
  for (let i = 0; i < ary.length; i++) {
    if (Array.isArray(ary[i])) {
      ret = ret.concat(flatArray(ary[i]));
    }
    else {
      ret.push(ary[i]);
    }
  }
  return ret as T;
};

export function wait(time = 1000) {
  return new Promise((resolve) => {
    setTimeout(resolve, time);
  });
}

export const swap = (json: any) => {
  const ret = {};
  for (const key in json) {
    ret[json[key]] = key;
  }
  return ret;
};

export function getFilenameFromContentDisposition(contentDisposition: string) {
  const match = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(contentDisposition);
  return match ? decodeURIComponent(match[1]) : null;
}

export const numCamaSeperate = (value: number | string) => {
  if (value == null) return null;
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const getChangedValues = (values, initialValues) => {
  return Object
    .entries(values)
    .reduce((acc, [key, value]) => {
      const hasChanged = initialValues[key] !== value;
      if (hasChanged) {
        acc[key] = value;
      }
      return acc;
    }, {});
};

export const formatDate = (date: string, targetFormat = 'jYYYY-jMM-jDD HH:mm:ss', readFormat = 'YYYY-MM-DD HH:mm:ss') => {
  if (!date) return date;
  return moment.from(date, 'en', readFormat).format(targetFormat);
};

export const fromUTC = (date: string) => {
  const locale = moment.locale();
  moment.locale('en');
  const result = moment(date.replace(' UTC', 'Z'));
  moment.locale(locale);
  return result;
};

export const formatUTC = (date: string, format: string = 'jYYYY/jMM/jDD HH:mm:ss') => {
  return fromUTC(date).format(format);
};

export function round(value: number, step?: number) {
  step || (step = 1.0);
  const inv = 1.0 / step;
  return Math.round(value * inv) / inv;
}

export async function convertBase64ToBlob(base64: string) {
  return fetch(base64).then(res => res.blob());
}

export async function convertBase64ToFile(base64: string, filename: string, type: string) {
  return convertBase64ToBlob(base64).then(blob => new File([blob], filename, { type }));
}

export function convertFileToBase64(file: RcFile | File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

export function getObjectDiff(obj1: any, obj2: any) {
  if (obj1 == null) return obj2 == null ? [] : '$ROOT$';
  const diff = Object.keys(obj1).reduce((result, key) => {
    if (!Object.prototype.hasOwnProperty.call(obj2, key)) {
      result.push(key);
    }
    else if (isEqual(obj1[key], obj2[key])) {
      const resultKeyIndex = result.indexOf(key);
      result.splice(resultKeyIndex, 1);
    }
    return result;
  }, Object.keys(obj2));

  return diff;
}

const func = {
  call: invoke,
  unify,
  range,
  swap,
  flatArray,
  getChangedValues,
  numCamaSeperate,
  formatDate,
  fromUTC,
  formatUTC,
  wait,
  round,
  convertBase64ToBlob,
  convertBase64ToFile,
  convertFileToBase64,
  getObjectDiff,
  getFilenameFromContentDisposition
};

export default func;
